import React, { useEffect, useMemo } from "react"
import { Comment, CommentableResource, LexicalText } from "@/schema"

import { useComments } from "@/hooks/api/comments"
import { useFetchCurrentUser } from "@/hooks/api/users"

import { CommentList } from "./comment-list"

const countComments = (comments: Comment[]): number => {
  return comments.reduce((total, comment) => {
    const replies = comment.replies || []
    return total + 1 + countComments(replies)
  }, 0)
}

interface CommentSectionProps {
  resourceType: CommentableResource["resourceType"]
  resourceId: string
  className?: string
  onCommentCountChange?: (count: number) => void
}

export function CommentSection({ resourceType, resourceId, className, onCommentCountChange }: CommentSectionProps) {
  const { data: currentUser } = useFetchCurrentUser()
  const { comments, isLoading, error, createComment, toggleReaction } = useComments(resourceType, resourceId)
  const commentCount = useMemo(() => countComments(comments), [comments])

  useEffect(() => {
    if (!onCommentCountChange) return
    onCommentCountChange(commentCount)
  }, [commentCount, onCommentCountChange])

  const handleCreateComment = (content: LexicalText, files: File[], parentId?: string) => {
    return createComment({
      commentableType: resourceType,
      commentableId: resourceId,
      content,
      files,
      parentId,
    })
  }

  const handleReaction = (commentId: string, emoji: string) => {
    toggleReaction({
      commentId,
      emoji,
    })
  }

  return (
    <CommentList
      comments={comments}
      onCreateComment={handleCreateComment}
      onReaction={handleReaction}
      currentUserId={currentUser?.id || ""}
      isLoading={isLoading}
      error={error as Error | null}
      className={className}
    />
  )
}
